import { useLocalSearchParams } from 'expo-router'
import { useEffect, useRef, type ReactNode } from 'react'
import { ActivityIndicator } from 'react-native'

import { useJoinCommunityMutation } from '@/src/features/community/services/useJoinCommunity'
import { useCommunityJoinSteps } from '@/src/features/community/ui/hooks/useCommunityJoinSteps'
import { CommunityNotificationPermissionsScreen } from '@/src/features/community/ui/screens/CommunityNotificationPermissionsScreen'
import { getNotificationsAvailable } from '@/src/features/notification/utils/getNotificationsAvailable'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { filterSearchParams } from '@/src/general/utils/filterSearchParams'
import { parseIdDash } from '@/src/general/utils/parseIdDash'
import { useTranslate } from '@/src/lang/useTranslate'

const NoNotifications = ({ idSlug, searchParams }: TSearchParamsParam & TIdSlugParam): ReactNode => {
  const [id] = parseIdDash(idSlug)
  const hasNavigated = useRef(false)
  const { t } = useTranslate()
  const {
    join,
    isPending: isMutationPending,
    isSuccess: isJoinSuccess,
    isError: isJoinError,
    error: joinError,
  } = useJoinCommunityMutation(id, searchParams)
  const {
    goToNextStep,
    community,
    isPending: isQueriesPending,
  } = useCommunityJoinSteps({ idSlug, stepName: 'push', searchParams })

  useEffect(() => {
    if (community != null && !community.hasJoined && !isMutationPending && !isJoinSuccess && !isJoinError) {
      join('ongoing')
    }
  }, [join, community, isMutationPending, isJoinSuccess, isJoinError])

  useEffect(() => {
    if (
      hasNavigated.current ||
      community == null ||
      isQueriesPending ||
      isMutationPending ||
      isJoinError ||
      (!community.hasJoined && !isJoinSuccess)
    )
      return

    hasNavigated.current = true
    goToNextStep()
  }, [community, goToNextStep, isJoinError, isJoinSuccess, isMutationPending, isQueriesPending])

  if (isMutationPending || isQueriesPending) return <ActivityIndicator />

  if (isJoinError)
    return (
      <ErrorMessage
        error={joinError}
        defaultErrorKey="common.error.unknown"
        button={[{ children: t('tab.error.try-again'), onPress: () => join('ongoing') }]}
      />
    )

  if (community != null && !community.hasJoined && !isJoinSuccess) return <ActivityIndicator />

  return null
}

const CommunityJoinNotificationsRouterPage = () => {
  const searchParams = useLocalSearchParams()
  const { idSlug } = filterSearchParams<TIdSlugParam>(searchParams)

  const canReceiveNotifications = getNotificationsAvailable()

  if (!idSlug) return null

  if (!canReceiveNotifications) return <NoNotifications idSlug={idSlug} searchParams={searchParams} />

  return <CommunityNotificationPermissionsScreen idSlug={idSlug} searchParams={searchParams} />
}

export default CommunityJoinNotificationsRouterPage
